import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ModuleManager } from 'src/shared/models/module-manager';
import { CxoneCallArrivalModuleData, CxoneCallArrivalLegendPositions } from './cxone-call-arrival.model';
import { ChartConfiguration } from 'chart.js';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CXOneCallArrivalService } from './cxone-call-arrival.service';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleManagerMenuItemType, ModuleManagerSubMenu } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import moment from 'moment';

@Component({
    selector: 'cxone-call-arrival',
    templateUrl: './cxone-call-arrival.component.html',
    styleUrls: ['./cxone-call-arrival.component.scss']
})
export class CXOneCallArrivalComponent implements OnInit {

    @Input() moduleManager: ModuleManager;
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @Input() moduleKey: string;

    localization: Map<string, string>;
    firstLoad = true;
    data: CxoneCallArrivalModuleData;
    dataLoading = false;
    dataLoadSuccess = false;
    noData = false;
    isNoMetric = false;
    intervalRangeTooWide = false;
    public state: CxoneCallArrivalLegendPositions;
    public chartConfig: ChartConfiguration;
    hourInterval = 4;
    endDateTime: string;
    legendItemsHidden = new Map();

    colorConstants = {
        darkGreen: '#278A33',
        lightGreen: '#75D580',
        darkPurple: '#5C2B83',
        lightPurple: '#A476D7',
        darkPink: '#B01E6C',
        lightPink: '#EE67AF',
    }

    tooltipBorderColor = {
        borderColor: "'#75D580', '#A476D7','#EE67AF'"
    }

    _animateUpdate = true;
    _datasetBase = {
        xAxisID: "x",
        yAxisID: "y",
        borderWidth: 2,
        pointRadius: 2,
        hitRadius: 4,
    };

    _chartConstants = {
        fontColor: "#353a40",
        fontFamily: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif",
    };

    constructor(
        private service: CXOneCallArrivalService,
        private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService,
    ) { }

    async ngOnInit(): Promise<void> {
        this.localization = await this.cvLocalizationService.GetSection("CXOne_Call_Arrival_Module");

        this.moduleManager.setOptions({
            onSettingsApplied: settings => this.loadData(false, true),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.CAS,
            onRefresh: isAutoRefresh => this.loadData(isAutoRefresh, false),
            canRefresh: () => !this.dataLoading,
        });

        this.moduleManager.addMenuItem({
            name: "Legend_Position",
            icon: 'legend',
            type: ModuleManagerMenuItemType.SubMenu,
            getEnabled: () => this.chartObject != null,
            subItems: [{
                name: "Top",
                icon: '',
                click: () => this.setLegendPosition('top'),
            },
            {
                name: "Bottom",
                icon: '',
                click: () => this.setLegendPosition('bottom')
            },
            {
                name: "Left",
                icon: '',
                click: () => this.setLegendPosition('left')
            },
            {
                name: "Right",
                icon: '',
                click: () => this.setLegendPosition('right')
            }]
        } as ModuleManagerSubMenu);

        this.moduleManager.addState({
            name: 'chartState',
            autoLoad: true,
            getStateData: () => this.state,
            onStateLoaded: (stateData) => {
                if (!stateData)
                    return;
                this.state = stateData;
                if (this.chartConfig) {
                    this.chartConfig.options.plugins.legend.position = this.state.legendPosition;
                }
            }
        });

    }

    updateIntervals() {
        if (this.moduleManager.customSettings.HourInterval) {
            this.hourInterval = this.moduleManager.customSettings.HourInterval.getValue();
        }
        if (this.moduleManager.customSettings.EndDateTime?.getValue()) {
            const dateTime = new Date(this.moduleManager.customSettings.EndDateTime.getValue());
            this.endDateTime = moment(dateTime).format('MMM D, YYYY h:mm A');
        } else {
            const today = new Date();
            const dateTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
            this.endDateTime = moment(dateTime).format('MMM D, YYYY h:mm A');
        }
    }

    async setLegendPosition(position: CxoneCallArrivalLegendPositions["legendPosition"]) {
        this.state.legendPosition = position;
        this.chartConfig.options.plugins.legend.position = position;
        this.loadData(false, false);
        await this.moduleManager.saveState('chartState');
        this.moduleManager.requestPublishNeeded();
    }

    createChart() {
        if (this.state == null || this.state.legendPosition == null) {
            this.state = {
                legendPosition: 'top'
            };
        }
        this.chartConfig = {
            type: "line",
            data: {
                datasets: [],
                labels: [],
            },
            options: {
                maintainAspectRatio: false,
                responsive: true,
                datasets: {
                    line: {
                        spanGaps: false,
                    },
                },
                elements: {
                    line: {
                        tension: 0.3
                    },
                    point: {
                        hitRadius: 4,
                        hoverRadius: 4,
                        radius: 0,
                    },
                },
                hover: {
                    intersect: true,
                    mode: "index",
                },
                layout: {
                    padding: {
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0,
                    },
                },
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne
                    },
                    title: {
                        display: false,
                        text: "",
                    },
                    legend: {
                        display: true,
                        position: this.state.legendPosition,
                        onClick: this.legendClickHandler.bind(this),
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12,
                            color: this._chartConstants.fontColor,
                            font: {
                                family: this._chartConstants.fontFamily,
                            }
                        },
                    },
                    tooltip: {
                        intersect: false,
                        mode: "index",
                        yAlign: "bottom",
                        position: "nearest",
                        padding: 8,
                        usePointStyle: true,
                        boxPadding: 4,
                        boxWidth: 10,
                        boxHeight: 10,
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        borderWidth: 1,
                        borderColor: 'rgba(208, 210, 211, 1)',
                        titleColor: '#8C95A5',
                        titleFont: {
                            size: 13,
                            family: 'OpenSans',
                            weight: 'normal',
                            lineHeight: '22px'
                        },
                        bodyFont: {
                            size: 13,
                            family: 'OpenSans',
                            weight: 'normal',
                            lineHeight: '22px'
                        },
                        bodyColor: '#2E2E2E',
                        cornerRadius: 0,
                        callbacks: {
                            title: (tooltipItem) => tooltipItem[0].label,
                            label: (tooltipItem) => (tooltipItem.dataset.label + ": " +
                                tooltipItem.dataset.data[tooltipItem.dataIndex]),
                            labelColor: tooltipItem => ({
                                borderColor: this.tooltipBorderColor.borderColor,
                                backgroundColor: tooltipItem.dataset.backgroundColor,
                            }),
                            labelPointStyle: context => ({
                                pointStyle: 'circle',
                                rotation: 0,
                            }),
                        }
                    },
                },
                scales: {
                    x: {
                        display: true,
                        title: {
                            display: false,
                            color: this._chartConstants.fontColor,
                            font: {
                                family: this._chartConstants.fontFamily,
                            },
                        },
                        ticks: {
                            autoSkip: true,
                            source: "labels",
                        },
                    },
                    y: {
                        display: true,
                        position: "left",
                        beginAtZero: true,
                        min: 0,
                        title: {
                            display: false,
                            color: this._chartConstants.fontColor,
                            font: {
                                family: this._chartConstants.fontFamily,
                            },
                        },
                    },
                },
            },
        } as ChartConfiguration;
    }

    legendClickHandler(e, item) {
        const index = item.datasetIndex;
        const meta = this.chartObject.chart.getDatasetMeta(index);

        meta.hidden = !this.legendItemsHidden.get(item.text);

        this.legendItemsHidden.set(item.text, meta.hidden);
        
        this.chartObject.chart.update();
    }

    async loadData(isAutoRefresh: boolean, updateInterval: boolean) {
        if (this.dataLoading)
            return;
        this.dataLoading = true;
        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }
        if (updateInterval) {
            this.updateIntervals();
        }
        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.firstLoad);
            if (!this.data) {
                this.noData = true;
                this.createChart();
            } else if (this.data.agentsOnCall.length + this.data.agentsWaiting.length + this.data.callsOnHold.length === 0) {
                this.noData = true;
                this.createChart();
                this.chartConfig.options.plugins.legend.display = false;
            } else {
                const showOnlyTime = this.data.showOnlyTime;
                this.data.xAxisLabels = this.data.xAxisLabels.map((dateString: string) => {
                    if (showOnlyTime) {
                        return dateString;
                    } else {
                        const formattedDate = moment(dateString, 'MM/DD/YYYY h:mm A').format('MMM D, YYYY h:mm A');
                        return formattedDate;
                    }
                });
                this.noData = false;
                this.createChart();
                this.updateChartData();
                this.moduleManager.setAutoRefreshReady();
            }
        }
        catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        }
        this.dataLoading = false;
        this.dataLoadSuccess = true;
        this.moduleManager.showLoader = false;
        this.firstLoad = false;
    };

    updateChartData() {
        if (this.chartConfig.data.datasets.length === 0) {
            let newDatasets = [
                {
                    label:
                        this.localization != null && this.localization.size != 0
                            ? this.localization.get("Agents_On_Calls")
                            : "Agents on Calls",
                    pointBorderColor: this.colorConstants.darkGreen,
                    pointBackgroundColor: this.colorConstants.darkGreen,
                    pointRadius: 1.5,
                    data: this.data?.agentsOnCall,
                    backgroundColor: this.colorConstants.lightGreen,
                    borderColor: this.colorConstants.darkGreen,
                    borderWidth: 1,
                    fill: "stack",
                    hidden: this.legendItemsHidden.get("Agents on calls")
                },
                {
                    label:
                        this.localization != null && this.localization.size != 0
                            ? this.localization.get("Agents_Waiting")
                            : "Agents Waiting",
                    pointBorderColor: this.colorConstants.darkPurple,
                    pointBackgroundColor: this.colorConstants.darkPurple,
                    pointRadius: 1.5,
                    data: this.data?.agentsWaiting,
                    backgroundColor: this.colorConstants.lightPurple,
                    borderColor: this.colorConstants.darkPurple,
                    borderWidth: 1,
                    fill: "stack",
                    hidden: this.legendItemsHidden.get("Agents waiting")
                },
                {
                    label:
                        this.localization != null && this.localization.size != 0
                            ? this.localization.get("Calls_In_Queue")
                            : "Calls in Queue",
                    pointBorderColor: this.colorConstants.darkPink,
                    pointBackgroundColor: this.colorConstants.darkPink,
                    pointRadius: 1.5,
                    data: this.data?.callsOnHold,
                    backgroundColor: this.colorConstants.lightPink,
                    borderColor: this.colorConstants.darkPink,
                    borderWidth: 1,
                    fill: "stack",
                    hidden: this.legendItemsHidden.get("Calls in queue")
                },
            ];
            newDatasets = newDatasets.map(ds => Object.assign({}, this._datasetBase, ds));
            this.chartConfig.data.datasets = newDatasets;
            this.chartConfig.data.labels = this.data.xAxisLabels;
        } else {
            this.chartConfig.data.datasets[0].data = this.data.agentsOnCall;
            this.chartConfig.data.datasets[0].backgroundColor = this.colorConstants.lightGreen;
            this.chartConfig.data.datasets[0].borderColor = this.colorConstants.darkGreen;
            this.chartConfig.data.datasets[0].borderWidth = 1;

            this.chartConfig.data.datasets[1].data = this.data.agentsWaiting;
            this.chartConfig.data.datasets[1].backgroundColor = this.colorConstants.lightPurple;
            this.chartConfig.data.datasets[1].borderColor = this.colorConstants.darkPurple;
            this.chartConfig.data.datasets[1].borderWidth = 1;

            this.chartConfig.data.datasets[2].data = this.data.callsOnHold;
            this.chartConfig.data.datasets[2].backgroundColor = this.colorConstants.lightPink;
            this.chartConfig.data.datasets[2].borderColor = this.colorConstants.darkPink;
            this.chartConfig.data.datasets[2].borderWidth = 1;
            this.chartConfig.data.labels = this.data.xAxisLabels;
        }
        this.chartConfig.options.plugins.legend.display = true;
        this._animateUpdate = false;
    }
}
